import { supabase } from './supabaseClient';

export const INTRO_SPLASH_V3_SEEN_KEY = 'mptIntroSplashV3Seen';
export const INTRO_SPLASH_V3_SEEN_AT_KEY = 'mptIntroSplashV3SeenAt';

const REMOTE_PREFERENCE_TIMEOUT_MS = 6000;

const withTimeout = async (promise, timeoutMs = REMOTE_PREFERENCE_TIMEOUT_MS) => {
  let timeoutId;

  const timeout = new Promise((resolve) => {
    timeoutId = setTimeout(() => {
      resolve({ timedOut: true });
    }, timeoutMs);
  });

  const result = await Promise.race([
    Promise.resolve(promise).then(
      (value) => ({ value }),
      (error) => ({ error })
    ),
    timeout,
  ]);

  clearTimeout(timeoutId);
  return result;
};

const isSeenValue = (value) => value === true || value === 'true' || value === 1 || value === '1';

export const hasSeenIntroSplashV3Locally = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return true;
    }

    return window.localStorage.getItem(INTRO_SPLASH_V3_SEEN_KEY) === 'true';
  } catch {
    return true;
  }
};

export const markIntroSplashV3SeenLocally = () => {
  const seenAt = new Date().toISOString();

  try {
    window.localStorage.setItem(INTRO_SPLASH_V3_SEEN_KEY, 'true');
    window.localStorage.setItem(INTRO_SPLASH_V3_SEEN_AT_KEY, seenAt);
  } catch {
    // Le stockage local est un confort : la session reste valide même s'il échoue.
  }
};

export const markGeneralSplashSessionSeen = () => {
  try {
    window.sessionStorage.setItem('generalSplashShown', 'true');
  } catch {
    // ignore storage errors
  }
};

export const getCurrentIntroSplashSession = async () => {
  if (!supabase?.auth?.getSession) {
    return null;
  }

  try {
    const result = await withTimeout(supabase.auth.getSession());
    if (result.error || result.timedOut) {
      return null;
    }

    return result.value?.data?.session ?? null;
  } catch {
    return null;
  }
};

export const onIntroSplashAuthStateChange = (handler) => {
  if (!supabase?.auth?.onAuthStateChange || typeof handler !== 'function') {
    return () => {};
  }

  try {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      handler(session);
    });

    return () => {
      data?.subscription?.unsubscribe?.();
    };
  } catch {
    return () => {};
  }
};

const getProfilePreferences = async (userId) => {
  if (!userId || !supabase?.from) {
    return null;
  }

  try {
    const query = supabase
      .from('profiles')
      .select('preferences')
      .eq('id', userId)
      .maybeSingle();

    const result = await withTimeout(query);
    if (result.error || result.timedOut || result.value?.error) {
      return null;
    }

    const preferences = result.value?.data?.preferences;
    return preferences && typeof preferences === 'object' ? preferences : {};
  } catch {
    return null;
  }
};

export const hasSeenIntroSplashV3Remotely = async (userId) => {
  const preferences = await getProfilePreferences(userId);
  if (!preferences) {
    return null;
  }

  return isSeenValue(preferences[INTRO_SPLASH_V3_SEEN_KEY]);
};

export const markIntroSplashV3SeenRemotely = async (userId) => {
  const preferences = await getProfilePreferences(userId);
  if (!preferences) {
    return false;
  }

  const nextPreferences = {
    ...preferences,
    [INTRO_SPLASH_V3_SEEN_KEY]: true,
    [INTRO_SPLASH_V3_SEEN_AT_KEY]:
      typeof preferences[INTRO_SPLASH_V3_SEEN_AT_KEY] === 'string'
        ? preferences[INTRO_SPLASH_V3_SEEN_AT_KEY]
        : new Date().toISOString(),
  };

  try {
    const update = supabase
      .from('profiles')
      .update({ preferences: nextPreferences })
      .eq('id', userId)
      .select('preferences')
      .maybeSingle();

    const result = await withTimeout(update);
    return !result.error && !result.timedOut && !result.value?.error;
  } catch {
    return false;
  }
};
